// Formats d'affichage partagés (spec 159, FR-012 et FR-013) — dates, montants, économies.
//
// Avant cette spec, le portail comptait ~57 fonctions de date locales et ~40 de montant, pour neuf rendus de date
// différents (« 22/09/2026 », « sept. 26 », « 22 septembre 2026 »…) et des « −0 € ». Ce module donne UN format par
// granularité. Il ne change AUCUNE valeur ni AUCUN arrondi (P4 de l'initiative, règle « Méthodes de calcul » du
// CLAUDE.md) : il reçoit les nombres que les écrans recevaient, et l'arrondi reste celui d'`Intl`, qu'ils employaient.
//
// Plateforme : ne nomme aucune solution ; les solutions l'importent (sens autorisé).

type DateInput = string | number | Date | null | undefined

const DAY = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
const MONTH = new Intl.DateTimeFormat('fr-FR', { month: 'short', year: 'numeric' })
const TIME = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })

/** Ce qui s'affiche pour une valeur absente ou illisible. */
export const MISSING = '—'

// Un jour civil sans heure (« 2026-03-01 » : date d'effet, échéance). `new Date('2026-03-01')` le lirait minuit UTC,
// donc la veille à l'ouest de Greenwich (revue à froid de l'incrément 3) : il se construit en date LOCALE. Une chaîne
// avec une heure est un instant, et garde la lecture standard.
const CIVIL_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  let d: Date
  if (value instanceof Date) d = value
  else {
    const day = typeof value === 'string' ? CIVIL_DAY.exec(value) : null
    d = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value)
  }
  return Number.isNaN(d.getTime()) ? null : d
}

/** Jour : « 14 oct. 2026 ». */
export function formatDate(value: DateInput): string {
  const d = toDate(value)
  return d ? DAY.format(d) : MISSING
}

/** Mois : « oct. 2026 » — y compris sur les axes de graphiques (« sept. 26 » se lisait « 26 septembre »). */
export function formatMonth(value: DateInput): string {
  const d = toDate(value)
  return d ? MONTH.format(d) : MISSING
}

/** Date et heure : « 14 oct. 2026, 14:30 ». Assemblées à la main : selon la version d'ICU, Intl écrit « à ». */
export function formatDateTime(value: DateInput): string {
  const d = toDate(value)
  return d ? `${DAY.format(d)}, ${TIME.format(d)}` : MISSING
}

export interface AmountOptions {
  /** Décimales affichées (0 par défaut). */
  decimals?: number
  /** Ajoute « € » (vrai par défaut). */
  currency?: boolean
  /** Affiche « + » devant un montant positif (faux par défaut). */
  signed?: boolean
}

const amountFormatters = new Map<number, Intl.NumberFormat>()
function amountFormatter(decimals: number): Intl.NumberFormat {
  let f = amountFormatters.get(decimals)
  if (!f) {
    f = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    amountFormatters.set(decimals, f)
  }
  return f
}

/**
 * Montant : « 18 480 € », « −1 200 € », jamais « −0 € ».
 *
 * Le « −0 » venait d'un test d'égalité à zéro fait AVANT l'arrondi : −0,4 n'est pas 0, mais s'affiche « 0 ». On ne
 * réarrondit pas pour le corriger (l'arrondi d'Intl et `Math.round` diffèrent sur −2,5) : on retire le signe d'un
 * résultat qui ne compte que des zéros.
 */
export function formatAmount(value: number | null | undefined, opts: AmountOptions = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return MISSING
  const { decimals = 0, currency = true, signed = false } = opts
  const abs = amountFormatter(decimals).format(Math.abs(value))
  const isZero = !/[1-9]/.test(abs)
  const sign = isZero ? '' : value < 0 ? '−' : signed ? '+' : ''
  return `${sign}${abs}${currency ? ' €' : ''}`
}

export interface SavingsOptions {
  decimals?: number
  /** Période affichée après l'unité : « €/mois », « €/an ». */
  period?: 'mois' | 'an'
}

/**
 * Économie lue en positif (FR-012) : « 3 800 €/mois d'économies ». Une économie NÉGATIVE est un surcoût, et se dit :
 * « 120 €/mois de surcoût » — elle ne se cache jamais derrière un signe. La valeur absolue est celle du montant signé
 * que l'écran affichait (P4).
 */
export function formatSavings(saving: number | null | undefined, opts: SavingsOptions = {}): string {
  if (saving === null || saving === undefined || !Number.isFinite(saving)) return MISSING
  const amount = formatAmount(Math.abs(saving), { decimals: opts.decimals ?? 0, currency: false })
  const unit = opts.period ? ` €/${opts.period}` : ' €'
  const isZero = !/[1-9]/.test(amount)
  return `${amount}${unit} ${saving < 0 && !isZero ? 'de surcoût' : 'd’économies'}`
}
